<template>
  <div class="min-h-[900px]">
    <main class="pb-24">
      <!-- 1. Hero (Bridge: ServicesCommonHome) – H1, "Increase Your Credit Score", image top-right, text -->
      <section
        class="flex flex-col items-center pt-0 md:pt-16 pb-12 lg:pt-[102px] lg:pb-16"
      >
        <div class="mx-auto min-w-[320px] max-w-container px-4">
          <h1
            class="landing_h1 text-2xl md:text-6xl mb-6 text-center md:text-left"
          >
            <Translate
              translate-key="page_data.tradelines-for-individuals.h1"
            />
          </h1>
          <div class="py-12 lg:py-16">
            <h2 class="font-bold text-xl mb-4 text-left">
              <Translate
                translate-key="tradelines_for_individuals.data.1.title"
              />
            </h2>
            <div
              class="prose prose-gray max-w-none text-left prose-p:mb-3 prose-ul:my-3 prose-li:my-0.5 prose-a:text-success prose-a:underline hover:prose-a:no-underline prose-strong:font-semibold"
            >
              <!-- Mobile: image full width, then text. Desktop: image floats right -->
              <img
                src="/boost/using-tradelines-to-improve-your-credit-score.png"
                :alt="t('tradelines_for_individuals.alt_1')"
                width="457"
                height="360"
                loading="lazy"
                class="w-full max-md:mb-4 md:float-right md:max-w-[340px] md:ml-6 md:mb-4 lg:max-w-[457px] rounded-lg object-cover"
              />
              <div
                class="text-gray-700"
                v-html="t('tradelines_for_individuals.data.1.content')"
              />
              <div class="clear-both hidden md:block" />
            </div>
          </div>
        </div>
      </section>

      <!-- 2. How it works – 1. Choose Your Tradelines, 2. Purchase, 3. Increase (Bridge: HowItWorksList) -->
      <LandingHowItWorksList />

      <!-- 3. Authorized User Tradelines Are Safe And Legal (Bridge: HowItWorksLastHome) -->
      <LandingHowItWorksLastHome />

      <!-- 4. Testimonials (Bridge: LandingTestimonials) -->
      <LazyLandingPageTestimonials
        v-if="testimonialsData?.length"
        :testimonials-data="testimonialsData"
      />

      <!-- 5. FAQ (Bridge: FaqSection) -->
      <FaqSection
        :title="t('tradelines_for_individuals.faq_title')"
        title-class="landing_h2"
        class="py-12"
      />

      <!-- 6. Get Your Personal Tradelines Right Now (Bridge: LandingBoostCredit) -->
      <LandingPageBoostCredit
        :title="t('tradelines_for_individuals.data.2.title')"
        :text="t('tradelines_for_individuals.data.2.content')"
      />
    </main>
  </div>
</template>
<script setup lang="ts">
import Translate from "~/shared/components/i18n/Translate/Translate.vue";
import FaqSection from "~/features/faq/components/FaqSection.vue";
import type { TestimonialItem } from "~/features/landing-page/composables/useLandingTestimonials";
import { useI18n } from "vue-i18n";
import { useAuthFetch } from "~/shared/composables/useAuthFetch";

definePageMeta({ layout: "public" });

const { t } = useI18n();
const config = useRuntimeConfig();
const { authFetch } = useAuthFetch();

const { data: testimonialsRaw } = await useAsyncData(
  "tradelines-for-individuals-testimonials",
  () =>
    authFetch<TestimonialItem[] | { results: TestimonialItem[] }>(
      `${config.public.baseURL}v1/testimonials/`,
      { params: { type: 1, limit: 3, offset: 0 } },
    ).catch(() => []),
);

const testimonialsData = computed(() => {
  const d = testimonialsRaw.value;
  return (Array.isArray(d) ? d : d?.results) ?? [];
});

const siteUrl = (config.public.siteUrl as string) || "";
const route = useRoute();

useSeoMeta({
  title: () => t("page_data.tradelines-for-individuals.seo_title"),
  description: () => t("page_data.tradelines-for-individuals.seo_description"),
  ogTitle: () => t("page_data.tradelines-for-individuals.seo_title"),
  ogDescription: () => t("page_data.tradelines-for-individuals.seo_description"),
  ogUrl: () => (siteUrl ? `${siteUrl}${route.path}` : undefined),
  ogImage: () => (siteUrl ? `${siteUrl}/boost/1-fb-pic.jpeg` : undefined),
  ogImageAlt: () => t("tradelines_for_individuals.og_image_alt"),
  titleTemplate: () => "%s | Get Credit Boost",
});
useHead(() => ({
  meta: [{ name: "keywords", content: t("page_data.tradelines-for-individuals.seo_keywords") }],
}));
</script>
